import React from 'react';
import {
  Box,
  IconButton,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'next-i18next';

import LocationModal from './LocationModal';
import LocationDropdown from './LocationDropdown';
import FilterDropdown from './FilterDropdown';
import SearchDropdown from './SearchDropdown';
import CustomDropdown from './CustomDropdown';

import {
  SearchContainer,
  SearchInput,
  SearchButton,
} from './styles';

import { DEFAULT_PRICE_RANGES, DEFAULT_AREA_RANGES } from './constants/searchConstants';

import { useApiData } from './hooks/useApiData';
import { useSearchBarState } from './hooks/useSearchBarState';
import { useLocationWithChildren } from './hooks/useLocationWithChildren';

import { createSearchHandlers } from './handlers/searchHandlers';
import { createModalHandlers } from './handlers/modalHandlers';
import { createFilterHandlers } from './handlers/filterHandlers';

const initialLocationCategories = [
  'location_categories.province',
  'location_categories.project_name', // เพิ่มชื่อโครงการเป็นตัวเลือก
  'location_categories.mass_transit',
  'location_categories.shopping_mall',
  'location_categories.university',
  'location_categories.road',
  'location_categories.hospital',
  'location_categories.park',
  'location_categories.airport',
  'location_categories.popular_location',
];

const SearchBar = () => {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language || 'th';

  const { 
    filteredActions, 
    actionMapping, 
    filteredCategories, 
    categoryMapping 
  } = useApiData(locale);

  const {
    searchText,
    setSearchText,
    transactionType,
    setTransactionType,
    propertyType,
    setPropertyType,
    priceMin,
    setPriceMin,
    priceMax,
    setPriceMax,
    minInputValue,
    setMinInputValue,
    maxInputValue,
    setMaxInputValue,

    locationAnchorEl,
    setLocationAnchorEl,
    selectedBedrooms,
    setSelectedBedrooms,
    selectedBathrooms,
    setSelectedBathrooms,
    
    areaMin,
    setAreaMin,
    areaMax,
    setAreaMax,
    areaMinInput,
    setAreaMinInput,
    areaMaxInput,
    setAreaMaxInput,

    selectedQuota,
    setSelectedQuota,
    selectedStatus,
    setSelectedStatus,
    selectedFloor,
    setSelectedFloor,
    selectedPublishedBy,
    setSelectedPublishedBy,
    selectedPostedDate,
    setSelectedPostedDate,
    selectedView,
    setSelectedView,
    selectedDirection,
    setSelectedDirection,

    selectedDynamicFilters,
    setSelectedDynamicFilters,

    openModal,
    setOpenModal,
    locationTypeId,
    setLocationTypeId,
    locationData,
    setLocationData,
    loadingLocationData,
    setLoadingLocationData,
    modalSearchValue,
    setModalSearchValue,

    selectedLocation,
    setSelectedLocation,
    selectedSubLocation,
    setSelectedSubLocation,
    locationSearchText,
    setLocationSearchText,
    locationTypes,
    setLocationTypes,

    modalView,
    setModalView,
    selectedMainOption,
    setSelectedMainOption,
    navigationHistory,
    setNavigationHistory,
    isSearchDisabled,
    setIsSearchDisabled,

    searchDebounceTimeout,
    page,
    setPage,
    hasMore,
    setHasMore,
    isLoadingMore,
    setIsLoadingMore,
    scrollRef,

    isSearching,
    setIsSearching,
    searchResults,
    setSearchResults,
    showDropdown,
    setShowDropdown,
    showLocationDropdown,
    setShowLocationDropdown,
    searchInputContainerRef,
    searchInputRef,
    locationWithChildren,
    setLocationWithChildren,

  } = useSearchBarState(t);

  const { locationWithChildren: preloadedLocationWithChildren } = useLocationWithChildren();

  const [apiQueryParams, setApiQueryParams] = React.useState<Record<string, any>>({});

  const getSelectedQueryParams = () => {
    return apiQueryParams;
  };

  const searchHandlers = createSearchHandlers({
    setSearchText,
    setTransactionType,
    setPropertyType,
    setSelectedLocation,
    setSelectedSubLocation,
    setIsSearchDisabled,
    setShowDropdown,
    setShowLocationDropdown,
    setModalSearchValue,
    setIsSearching,
    setSearchResults,
    setHasMore,
    setPage,
    setIsLoadingMore,
    setOpenModal,
    setLocationTypeId,
    setLocationData,
    setLoadingLocationData,
    setLocationSearchText,
    setNavigationHistory,
    setModalView,
    setSelectedMainOption,

    searchText,
    transactionType,
    propertyType,
    priceMin,
    priceMax,
    selectedLocation,
    selectedSubLocation,
    searchResults,
    page,
    hasMore,
    isSearching,
    isLoadingMore,
    locationTypeId,
    locationData,
    modalSearchValue,
    selectedMainOption,
    navigationHistory,
    searchDebounceTimeout,

    selectedBedrooms,
    selectedBathrooms,
    selectedQuota,
    selectedStatus,
    selectedFloor,
    selectedView,
    selectedDirection,
    selectedPublishedBy,
    selectedPostedDate,
    areaMin,
    areaMax,

    selectedDynamicFilters,

    getSelectedQueryParams,

    filteredActions,
    actionMapping,
    filteredCategories,
    categoryMapping,

    locale,
    t
  });

  const modalHandlers = createModalHandlers({
    setOpenModal,
    setNavigationHistory,
    setSelectedMainOption,
    setModalView,
    setModalSearchValue,
    setLoadingLocationData,
    setIsLoadingMore,
    setLocationData,
    setHasMore,
    setPage,
    setSelectedLocation,
    setSelectedSubLocation,
    setSearchText,
    setShowDropdown,

    navigationHistory,
    selectedMainOption,
    modalView,
    modalSearchValue,
    locationTypeId,
    transactionType,
    propertyType,
    locationData,
    page,
    hasMore,
    loadingLocationData,
    isLoadingMore,

    getActionIdFromType: searchHandlers.getActionIdFromType,
    getCategoryIdFromType: searchHandlers.getCategoryIdFromType,
    loadLocationDataBySearch: searchHandlers.loadLocationDataBySearch,
    
    locale
  });

  const handleDynamicFilterChange = (groupId: number, optionId: number | null, queryParams: Record<string, any>) => {
    setSelectedDynamicFilters(prev => {
      const newFilters = { ...prev };
      
      if (optionId === null) {
        delete newFilters[groupId];
      } else {
        newFilters[groupId] = optionId;
      }
      
      return newFilters;
    });

    setApiQueryParams(prev => {
      const newParams = { ...prev };
      
      if (optionId === null) {
        Object.keys(newParams).forEach(key => {
          if (key.includes(`_${groupId}_`) || key.includes(`${groupId}`)) {
            delete newParams[key];
          }
        });
      } else {
        Object.assign(newParams, queryParams);
      }
      
      return newParams;
    });
  };

  const filterHandlers = createFilterHandlers({
    setLocationAnchorEl,
    setPriceMin,
    setPriceMax,
    setMinInputValue,
    setMaxInputValue,
    setSelectedBedrooms,
    setSelectedBathrooms,
    setSelectedQuota,
    setSelectedStatus,
    setSelectedFloor,
    setSelectedPublishedBy,
    setSelectedPostedDate,
    setSelectedView,
    setSelectedDirection,
    setAreaMin,
    setAreaMax,
    setAreaMinInput,
    setAreaMaxInput,

    priceMin,
    priceMax,
    areaMin,
    areaMax,
    
  });

  return (
    <SearchContainer>
      <CustomDropdown
        options={filteredActions.map(action => ({
          id: action.id,
          value: action.action,
          label: action.action
        }))}
        value={transactionType}
        onChange={searchHandlers.handleTransactionTypeChange}
        placeholder={t('select_transaction')}
        width={120}
      />

      <CustomDropdown
        options={filteredCategories.map(category => {
          let displayValue;
          if (locale === 'en') {
            displayValue = category.category_Name_EN;
          } else if (locale === 'cn') {
            displayValue = category.category_Name_CN;
          } else {
            displayValue = category.category_Name_TH;
          }
          
          return {
            id: category.category_ID,
            value: displayValue || '',
            label: displayValue || ''
          };
        }).filter(cat => cat.value !== '')}
        value={propertyType}
        onChange={searchHandlers.handlePropertyTypeChange}
        placeholder={t('estate')}
        width={120}
        firstOption={t('estate')}
      />

      <Box sx={{ position: 'relative', flex: 1, width: '100%' }} ref={searchInputContainerRef}>
        <SearchInput
          placeholder={t('search_placeholder')}
          value={searchText}
          onChange={searchHandlers.handleSearchTextChange}
          onClick={searchHandlers.handleSearchInputClick}
          endAdornment={
            searchText && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={searchHandlers.handleClearSearch}
                  sx={{ color: '#919192' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }
          inputRef={searchInputRef}
        />

        <SearchDropdown
          isVisible={showDropdown}
          items={searchResults}
          loading={isSearching}
          searchText={searchText}
          onItemClick={searchHandlers.handleDropdownItemClick}
          anchorEl={searchInputRef.current}
          hasMore={hasMore}
          onScroll={searchHandlers.handleDropdownScroll}
          onClose={() => setShowDropdown(false)}
        />

        <LocationDropdown
          isVisible={showLocationDropdown}
          locationCategories={initialLocationCategories.map(category => t(category))}
          onLocationClick={searchHandlers.handleLocationTypeClick}
          anchorEl={searchInputRef.current}
          onClose={() => setShowLocationDropdown(false)}
        />
      </Box>

      <FilterDropdown
        priceMin={priceMin}
        priceMax={priceMax}
        minInputValue={minInputValue}
        maxInputValue={maxInputValue}
        priceRangesMin={DEFAULT_PRICE_RANGES.MIN}
        priceRangesMax={DEFAULT_PRICE_RANGES.MAX}
        onMinInputChange={filterHandlers.handleMinInputChange}
        onMaxInputChange={filterHandlers.handleMaxInputChange}
        onMinPriceSelect={filterHandlers.handleMinPriceSelect}
        onMaxPriceSelect={filterHandlers.handleMaxPriceSelect}
        onApply={filterHandlers.handleFilterApply}
        onReset={filterHandlers.handleFilterReset}
        selectedBedrooms={selectedBedrooms}
        onBedroomSelect={setSelectedBedrooms}
        selectedBathrooms={selectedBathrooms}
        onBathroomSelect={setSelectedBathrooms}
        areaMin={areaMin}
        areaMax={areaMax}
        areaMinInput={areaMinInput}
        areaMaxInput={areaMaxInput}
        areaRangesMin={DEFAULT_AREA_RANGES.MIN}
        areaRangesMax={DEFAULT_AREA_RANGES.MAX}
        onAreaMinInputChange={filterHandlers.handleAreaMinInputChange}
        onAreaMaxInputChange={filterHandlers.handleAreaMaxInputChange}
        onAreaMinSelect={filterHandlers.handleAreaMinSelect}
        onAreaMaxSelect={filterHandlers.handleAreaMaxSelect}
        selectedQuota={selectedQuota}
        onQuotaSelect={setSelectedQuota}
        selectedStatus={selectedStatus}
        onStatusSelect={setSelectedStatus}
        selectedFloor={selectedFloor}
        onFloorSelect={setSelectedFloor}
        selectedPublishedBy={selectedPublishedBy}
        onPublishedBySelect={setSelectedPublishedBy}
        selectedPostedDate={selectedPostedDate}
        onPostedDateSelect={setSelectedPostedDate}
        selectedView={selectedView}
        onViewSelect={setSelectedView}
        selectedDirection={selectedDirection}
        onDirectionSelect={setSelectedDirection}
        selectedAction={transactionType}
        selectedCategory={propertyType}
        apiFilters={undefined} // จะมาจาก API ในอนาคต
        onDynamicFilterChange={handleDynamicFilterChange}
        selectedLocation={selectedLocation}
        selectedSubLocation={selectedSubLocation}
        locationTypeId={locationTypeId}
      />

      <SearchButton
        variant="contained"
        onClick={searchHandlers.handleSearch}
      >
        {t('search')}
      </SearchButton>

      <LocationModal
        open={openModal}
        onClose={modalHandlers.handleModalClose}
        locationSearchText={locationSearchText}
        onSearchChange={modalHandlers.handleLocationSearch}
        loadingLocationData={loadingLocationData}
        locationData={locationData}
        modalView={modalView}
        selectedMainOption={selectedMainOption}
        onMainOptionClick={modalHandlers.handleMainOptionClick}
        onBackToMain={modalHandlers.handleBackToMain}
        onSelectSubLocation={modalHandlers.handleSelectSubLocation}
        onClearSearch={searchHandlers.handleClearSearch}
        onSaveSettings={modalHandlers.handleSaveSettings}
        isSearchDisabled={isSearchDisabled}
        searchValue={modalSearchValue}
        hasSubLocations={modalHandlers.hasSubLocations}
        onScroll={modalHandlers.handleScroll}
        isLoadingMore={isLoadingMore}
        onFinalSelect={modalHandlers.handleFinalSelect}
      />
    </SearchContainer>
  );
};

export default SearchBar; 